/**
 * PDP Color Switch — instant colour switching on the fashion PDP without a page reload.
 *
 * A delegated click on a colour swatch fetches the server-rendered fragments from
 * /products/{handle}?view=pdp-switch (templates/product.pdp-switch.liquid) plus product data via
 * OAProductsApi for the attribute-level updates, swaps the fragments into the live DOM, re-binds
 * the handlers the swaps destroyed, pushes history and dispatches `oa:colorswitch`. Any failure
 * falls back to normal navigation.
 *
 * Rendering the real Liquid snippets server-side keeps every shop / language / length / duallogo /
 * early-access permutation identical to a normal paint; the JS-rebuilt markup this replaced
 * hardcoded its translations and drifted from the template.
 *
 * Depends on OAProductsApi (oa-products-api.js), jQuery and Swiper. Public API:
 * window.oaPdpColorSwitch.
 */
(function() {
  'use strict';

  var currentHandle = null;
  var switching = false;
  // Colour clicked while a switch was running; applied once that one lands. onSwatchClick has
  // already suppressed the navigation, so dropping it would leave the swatch doing nothing.
  var queuedHandle = null;
  var queuedOpts = null;
  // Set when the queued colour arrived through Back or Forward: the running switch must not push
  // a history entry over the URL the browser has already moved.
  var suppressPush = false;

  // The colour currently on screen, so the delegated handlers bound once in init() read its
  // variants rather than the ones the page was served with.
  var currentProduct = null;

  var SWITCH_VIEW = '?view=pdp-switch';

  // handle -> { at, promise }. Warmed on intent (onSwatchIntent), so a click reuses an in-flight
  // or resolved fetch instead of starting its own.
  var switchCache = {};
  var lastPrefetch = null;

  // Fragments carry size availability and prices, so an entry kept for the whole session would
  // keep showing a size as buyable long after it sold out.
  var CACHE_TTL_MS = 5 * 60 * 1000;

  // Above any colour family a shopper works through in one visit, low enough to stay bounded.
  var CACHE_MAX = 12;

  // --- Helpers ---

  function getHandleFromUrl() {
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // Fetch (and memoise) the switch fragment. The switch is gated on this GET, so warming it on
  // intent is what makes a click feel instant — the GraphQL data alone isn't enough. Dedupes
  // through switchCache, so a hover-started request in flight is reused rather than refired.
  function fetchSwitchHtml(handle) {
    var entry = switchCache[handle];
    if (entry && (Date.now() - entry.at) < CACHE_TTL_MS) return entry.promise;

    // Failures are evicted, not cached: one 5xx on a hover prefetch the shopper never sees would
    // otherwise poison the handle for the full TTL and force a full page load on every retry.
    var promise = fetch('/products/' + encodeURIComponent(handle) + SWITCH_VIEW, { credentials: 'same-origin' })
      .then(function(r) { return r.ok ? r.text() : null; })
      .catch(function() { return null; })
      .then(function(html) {
        if (!html && switchCache[handle] && switchCache[handle].promise === promise) {
          delete switchCache[handle];
        }
        return html;
      });

    switchCache[handle] = { at: Date.now(), promise: promise };
    pruneCache();
    return promise;
  }

  // Expiry alone reclaims nothing — an entry is only TTL-checked when its own handle is read
  // again, and a pointer sweeping a 30-colour rail warms 30 fragments nobody comes back to. Runs
  // on write: drop the expired, then cap to the most recently fetched. Evicting an in-flight
  // entry is safe — switchColor holds its own reference to the promise.
  function pruneCache() {
    var now = Date.now();
    var handles = Object.keys(switchCache);
    for (var i = 0; i < handles.length; i++) {
      if ((now - switchCache[handles[i]].at) >= CACHE_TTL_MS) delete switchCache[handles[i]];
    }

    handles = Object.keys(switchCache);
    if (handles.length <= CACHE_MAX) return;
    handles.sort(function(a, b) { return switchCache[a].at - switchCache[b].at; });
    for (var j = 0; j < handles.length - CACHE_MAX; j++) delete switchCache[handles[j]];
  }

  function hasTag(tags, tag) {
    if (!tags) return false;
    if (Array.isArray(tags)) return tags.indexOf(tag) !== -1;
    return false;
  }

  // The two PDP swatch rails, matched by id — which deliberately excludes the quick-add sidebar's
  // rails (#fashion-color-switch-sidebar and friends): those keep their own
  // sidebar.fashionColorChange handler and their own selection state.
  var SWATCH_RAILS = ['#fashion-color-switch', '#fashion-color-limited-switch'];
  var SWATCH_CONTAINERS = SWATCH_RAILS.join(', ');

  // Qualify a selector against both rails, e.g. '#a .item, #b .item'.
  function railScope(selector) {
    return SWATCH_RAILS.map(function(id) { return id + ' ' + selector; }).join(', ');
  }

  // Swatches carry the handle bare (the JS-rendered rails) or as /products/{handle} (the Liquid
  // one), so both forms are normalised before comparing.
  function normalizeHandle(raw) {
    return String(raw || '').replace(/^.*\/products\//, '').replace(/[?#].*$/, '');
  }

  // Whether the rail holding this colour is the limited-edition one, which decides which of the
  // three colour-name labels is the visible one.
  function isLimitedHandle(handle) {
    var items = document.querySelectorAll('#fashion-color-limited-switch .sidebar--color-item-bg[data-handle]');
    for (var i = 0; i < items.length; i++) {
      if (normalizeHandle(items[i].getAttribute('data-handle')) === handle) return true;
    }
    return false;
  }

  // --- Fragment swaps (source: templates/product.pdp-switch.liquid) ---

  // Copy a fragment region's innerHTML onto its live counterpart. Returns the live element, or
  // null when either side is missing — this colour simply doesn't render that region.
  function swapHtml(doc, srcSelector, targetSelector) {
    var src = doc.querySelector(srcSelector);
    var target = document.querySelector(targetSelector);
    if (!src || !target) return null;
    target.innerHTML = src.innerHTML;
    return target;
  }

  // fashion-color-variant-list.liquid renders three colour-name labels — the "Color:" line and
  // the limited / classic headings the limited-edition layout switches to — and only one set is
  // visible at a time. index.layout.js's changeActiveColor keeps all three in step; so does this.
  function swapColorName(doc, handle) {
    var src = doc.getElementById('oa-switch-colorname');
    if (!src) return;
    var name = ' ' + src.textContent.trim();
    var limited = isLimitedHandle(handle);

    setLabelText('fashion-color-switch-item-title', limited ? '' : name);
    setLabelText('regular-color-text', limited ? '' : name);
    setLabelText('limited-color-text', limited ? name : '');
  }

  function setLabelText(id, text) {
    var el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  // Badge, h1 and colour subline — all colour-dependent and all inside .product-top-wrapper, so
  // one region rather than three updaters.
  function swapTop(doc) {
    swapHtml(doc, '#oa-switch-top', '.fashion-info-sticky .product-top-wrapper');
  }

  // The trail's last entry is the product and its category link is derived from the product's
  // tags, so it must be re-rendered for the new colour.
  function swapBreadcrumb(doc) {
    swapHtml(doc, '#breadcrumb', '#breadcrumb');
  }

  function swapDesktopImages(doc) {
    var t = swapHtml(doc, '#oa-switch-desktop-images .oa-fashion__product-image-container',
                          '.oa-fashion__product-image-container');
    if (!t) return;
    // The fragment's first image is eager, so the hero paints on insertion; the rest wait for the
    // observer, as on mobile. update() re-queries the document for .lazy and does reach nodes added
    // by innerHTML — forcing all six pulled the whole gallery for every colour hovered.
    if (window.lazyLoadInstance && typeof window.lazyLoadInstance.update === 'function') {
      window.lazyLoadInstance.update();
    }
  }

  // The zoom overlay's gallery, painted outside every swap region. Desktop half only —
  // #product_zoom_mobile holds initMobile()'s PinchZoom container in a closure, and showMobile
  // clones the live <picture> anyway. Left lazy: these masters are under the Swiper's own
  // LazyLoad, and forcing src would pull the full-resolution gallery for every colour hovered.
  function swapZoomGallery(doc) {
    var src = doc.querySelector('#oa-switch-zoom .oa-product-zoom-desktop');
    var target = document.querySelector('#product_zoom .oa-product-zoom-desktop');
    if (!src || !target) return;

    // initDesktop() early-returns while a Swiper exists, so leaving it would hand the new colour
    // an uninitialised stack of slides with no thumbs rail and no lazy loading.
    if (window.oaProductZoom && typeof window.oaProductZoom.resetDesktop === 'function') {
      window.oaProductZoom.resetDesktop();
    }

    target.innerHTML = src.innerHTML;
  }

  function swapMobileImages(doc) {
    var src = doc.getElementById('oa-switch-mobile-images');
    var target = document.getElementById('oa-switch-mobile-images');
    if (!src || !target) return;

    // Tear the Swipers down before replacing the DOM they are bound to.
    destroyMobileSlider();
    target.innerHTML = src.innerHTML;

    // Left lazy for initMobileSlider()'s Swiper LazyLoad below; forcing src would pull all 6-10
    // full-size images per colour hovered. The videos are the page-level instance's, which does
    // need a nudge to notice nodes added by innerHTML.
    if (window.lazyLoadInstance && typeof window.lazyLoadInstance.update === 'function') {
      window.lazyLoadInstance.update();
    }

    initMobileSlider();
  }

  // Size / length / logo picker, outside the colour rail so the swatches survive. FitAnalytics
  // injects #fitanalytics__button in here and never rebuilds it, so the live node is carried
  // across rather than swapped away.
  function swapSizes(doc) {
    var region = document.getElementById('oa-switch-sizes');
    var fitaBtn = region && region.querySelector('#fitanalytics__button');
    if (fitaBtn && fitaBtn.parentNode) fitaBtn.parentNode.removeChild(fitaBtn);

    var target = swapHtml(doc, '#oa-switch-sizes', '#oa-switch-sizes');

    if (fitaBtn && target) {
      // Back where the integration put it, or failing that at the end of the picker.
      var container = target.querySelector('.product-sizes-container') || target;
      container.appendChild(fitaBtn);
    }
  }

  function swapPrice(doc) {
    swapHtml(doc, '#oa-switch-price', '.oa-product_price-section__col-price');
  }

  // Guest reward points. Outside .product-top-wrapper and outside the price section, so no other
  // swap reaches it. Absent for logged-in shoppers on both sides, where this is a no-op by design.
  function swapPoints(doc) {
    swapHtml(doc, '#oa-switch-points', '#oa-switch-points');
  }

  function swapStock(doc) {
    swapHtml(doc, '#oa-switch-stock', '.oa-product_price-section__col-infos');
  }

  // Both blocks come from product-fashion-addtocart.liquid, the same snippet the template
  // renders, so every server-side state survives: buyable vs. sold out, the coming-soon
  // suppression, the buy-3 offer and the localised labels.
  var ATC_IDS = ['oa-switch-atc-desktop', 'oa-switch-atc-sticky'];

  // Nothing is added on top of the server render, `early-access-cta` in particular: its readers
  // test $('.oa-product-addtocart-btn').hasClass(), true if *any* button carries it, so
  // re-applying it here bypassed the "choose your size" guard in the complete-the-set modal.
  function swapAtc(doc) {
    for (var i = 0; i < ATC_IDS.length; i++) {
      swapHtml(doc, '#' + ATC_IDS[i], '#' + ATC_IDS[i]);
    }
  }

  // Qualify a selector against both add-to-cart blocks, e.g. '#a .btn, #b .btn'.
  function atcScope(selector) {
    return ATC_IDS.map(function(id) { return '#' + id + ' ' + selector; }).join(', ');
  }

  // Description tabs. product-fashion-content.liquid keys all of it on product.tags and the
  // colour half of the title — key features, the model line, the sheerness and material notices
  // — so replacing .desktop-tab wholesale is what covers the lot.
  function swapTabs(doc) {
    swapHtml(doc, '#oa-switch-tabs .desktop-tab', '.desktop-tab');
  }

  // Head metadata: description, og/twitter share tags and the Product structured data, so what
  // scrapes or shares the rewritten URL describes the right colour. Updated in place by
  // property/name rather than replacing the head, which also holds app scripts and styles.
  function swapHeadMeta(doc) {
    var src = doc.getElementById('oa-switch-head');
    if (!src) return;

    // Paired in document order, key by key: a key is not unique (theme-meta-info emits five
    // <meta name="google-site-verification">), so a per-key head.querySelector collapsed the
    // whole group onto the first match. Indexing also keeps the key out of a selector string,
    // where a quote would throw out of querySelector and abort the entire head update.
    var live = indexMetasByKey(document.head.querySelectorAll('meta[property], meta[name]'));
    var incoming = indexMetasByKey(src.querySelectorAll('meta[property], meta[name]'));

    for (var key in incoming) {
      if (!Object.prototype.hasOwnProperty.call(incoming, key)) continue;
      var group = incoming[key];
      var liveGroup = live[key] || [];
      for (var i = 0; i < group.length; i++) {
        if (liveGroup[i]) {
          liveGroup[i].setAttribute('content', group[i].getAttribute('content') || '');
        } else {
          document.head.appendChild(group[i].cloneNode(true));
        }
      }
    }

    // Only the Product blob is colour-dependent; the WebSite / Organization / breadcrumb blobs
    // alongside it are matched by type so the others are left alone.
    var newLd = findProductLdJson(src);
    var liveLd = findProductLdJson(document);
    if (newLd && liveLd) liveLd.textContent = newLd.textContent;
  }

  // api.pageTagging() scrapes .nosto_product out of the DOM, so this block decides which product
  // updateTracking's loadRecommendations() is answered about — and must run before it asks. The
  // answer arrives through Nosto's postrender event, as on a normal page load.
  function swapNostoTagging(doc) {
    swapHtml(doc, '#oa-switch-nosto .nosto_product', '.nosto_product');
  }

  // Group <meta> nodes by 'property|name' + its value, preserving document order within a group.
  function indexMetasByKey(metas) {
    var byKey = {};
    for (var i = 0; i < metas.length; i++) {
      var attr = metas[i].hasAttribute('property') ? 'property' : 'name';
      var value = metas[i].getAttribute(attr);
      if (!value) continue;
      var key = attr + '|' + value;
      (byKey[key] = byKey[key] || []).push(metas[i]);
    }
    return byKey;
  }

  function findProductLdJson(root) {
    var scripts = root.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var data = JSON.parse(scripts[i].textContent);
        if (data && data['@type'] === 'Product') return scripts[i];
      } catch (e) {
        /* a malformed blob is not the one we're looking for */
      }
    }
    return null;
  }

  // Refresh FitAnalytics. The widget is a click-triggered popup, so re-running the server-rendered
  // definitions (fit-finder-data) to replace window.fitAnalyticsData wholesale — id, preview
  // image, sizes, availability — and repointing the integration is enough.
  function swapFitFinder(doc) {
    var src = doc.getElementById('oa-switch-fitfinder');
    if (!src || !window.jQuery) return;

    // Drop the previously injected block so its <script> nodes don't accumulate on every switch.
    var prev = document.getElementById('oa-fitfinder-injected');
    if (prev && prev.parentNode) prev.parentNode.removeChild(prev);

    // fit-finder-data.liquid emits nothing for accessories, so an empty region means this colour
    // has no size finder. Repointing would leave the widget answering for the colour just left.
    if (!src.querySelector('script')) {
      stopFitAnalytics();
      return;
    }

    var container = document.createElement('div');
    container.id = 'oa-fitfinder-injected';
    document.body.appendChild(container);

    // jQuery executes the injected <script>s, redefining window.fitAnalyticsData.
    jQuery(container).append(src.innerHTML);

    reloadFitAnalytics(String((window.fitAnalyticsData && window.fitAnalyticsData.currentProductId) || ''));
  }

  // Take the widget down, the state a colour with no fit-finder definitions is served in.
  function stopFitAnalytics() {
    window.fitAnalyticsData = null;
    var btn = document.querySelector('.fitanalytics__button, #fitanalytics__button');
    if (btn && btn.parentNode) btn.parentNode.removeChild(btn);
    var integ = window._fitAnalytics && window._fitAnalytics.integration;
    if (integ && typeof integ.stop === 'function') {
      try { integ.stop(); } catch (e) { /* already stopped */ }
    }
  }

  // Prefer updateCurrentProductId(): it repoints the widget without removing the button, where a
  // stop()/start() rebuild collapses its box for a beat. fitAnalyticsData is already fully
  // replaced by now and the widget reads it live when opened, so the in-place update is correct.
  function reloadFitAnalytics(serial) {
    var integ = window._fitAnalytics && window._fitAnalytics.integration;
    if (!integ) return;
    try {
      if (serial && typeof integ.updateCurrentProductId === 'function') {
        integ.updateCurrentProductId(serial);
      } else if (typeof integ.start === 'function') {
        var btn = document.querySelector('.fitanalytics__button, #fitanalytics__button');
        if (btn && btn.parentNode) btn.parentNode.removeChild(btn);
        if (typeof integ.stop === 'function') { try { integ.stop(); } catch (e2) {} }
        integ.start();
      }
    } catch (e) {
      /* leave the previous widget in place if no reinit path is available */
    }
  }

  // --- Updaters ---

  function updateUrl(handle, pushState, doc) {
    var newUrl = '/products/' + handle;
    if (pushState !== false) {
      history.pushState({ handle: handle }, '', newUrl);
    }
    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = window.location.origin + newUrl;

    // page_title from the fragment, not rebuilt from the DOM, so a switched-to colour and a
    // reload of the same URL agree. Left alone if absent — a stale title beats a mangled one.
    var titleEl = doc && doc.getElementById('oa-switch-meta-title');
    var title = titleEl ? titleEl.textContent.trim() : '';
    if (title) document.title = title;
  }

  // "Complete the look" re-resolves itself: product-complete-the-look.liquid listens for
  // oa:colorswitch. Handled there because the custom path's query is built from the Liquid tag
  // lists that only exist in that file — resetting the handle from here re-ran the *previous*
  // colour's query.

  // Both loops are scoped to the PDP's own rails: the quick-add sidebar paints the same classes
  // and holds its own selection, which the document-wide query this replaced kept clearing.
  function updateActiveColorSwatch(handle) {
    var swatches = document.querySelectorAll(railScope('.item') + ', ' + railScope('.sidebar--color-item-box'));
    for (var i = 0; i < swatches.length; i++) {
      swatches[i].classList.remove('active');
      var link = swatches[i].querySelector('a[id^="variant-"]');
      if (link && link.id === 'variant-' + handle) {
        swatches[i].classList.add('active');
      }
    }
    // createHtml puts .active on the anchor, with the handle on the swatch inside it.
    var items = document.querySelectorAll(railScope('.sidebar--color-item-bg[data-handle]'));
    for (var j = 0; j < items.length; j++) {
      var parent = items[j].closest('.sidebar--color-item');
      if (!parent) continue;
      parent.classList.toggle('active', normalizeHandle(items[j].getAttribute('data-handle')) === handle);
    }
  }

  // Scoped to the add-to-cart form: data-productid is not this product's alone — the
  // complete-the-set modal, the back-in-stock sidebar and the flavour selects carry one too, and
  // rewriting the lot made them report this product's id. The inputs inside #oa-switch-sizes
  // arrive correct from the fragment; the form's own attribute is the one nothing else updates
  // (ajaxify-cart.liquid:122 reads it for tracking).
  function updateFormProductId(product) {
    var form = document.getElementById('add-to-cart-form');
    if (!form) return;
    form.setAttribute('data-productid', product.id);

    var fields = form.querySelectorAll('select[data-productid], input[data-productid]');
    for (var i = 0; i < fields.length; i++) {
      fields[i].setAttribute('data-productid', product.id);
    }
  }


  // The thumbs rail is optional — product-image-slider-mobile.liquid only renders it for
  // multi-image colours — so an unanchored '[id$="_thumbs"]' fell through to the desktop zoom
  // gallery's rail (product-image-zoom.liquid:39) and re-initialised it with mobile settings.
  function mobileThumbsEl() {
    var region = document.getElementById('oa-switch-mobile-images');
    return region ? region.querySelector('[id$="_thumbs"]') : null;
  }

  // (true, true) also strips the classes and inline styles Swiper added, so the fresh markup
  // initialises from a clean slate.
  function destroyMobileSlider() {
    var sliderEl = document.getElementById('product_image_slider_mobile');
    if (sliderEl && sliderEl.swiper) sliderEl.swiper.destroy(true, true);
    var thumbsEl = mobileThumbsEl();
    if (thumbsEl && thumbsEl.swiper) thumbsEl.swiper.destroy(true, true);
  }

  function initMobileSlider() {
    if (typeof Swiper === 'undefined') return;
    var sliderEl = document.getElementById('product_image_slider_mobile');
    if (!sliderEl) return;

    var thumbsEl = mobileThumbsEl();
    var thumbs = {};

    if (thumbsEl) {
      var thumbSlides = thumbsEl.querySelectorAll('.swiper-slide:not(.hidden)');
      var thumbsPerView = Math.min(thumbSlides.length, 5) || 5;
      var thumbsSwiper = new Swiper(thumbsEl, {
        spaceBetween: 5,
        slidesPerView: thumbsPerView,
        watchSlidesProgress: true,
        lazyLoad: true
      });
      thumbs = { swiper: thumbsSwiper };
    }

    new Swiper(sliderEl, {
      on: {
        click: function(event) {
          if (window.oaProductZoom) window.oaProductZoom.show(event.target);
        }
      },
      navigation: {
        nextEl: '#product_image_slider_mobile .oa-product-image-slider__nav-next',
        prevEl: '#product_image_slider_mobile .oa-product-image-slider__nav-prev',
        disabledClass: 'oa-product-image-slider__nav--disabled',
        hiddenClass: 'oa-product-image-slider__nav--disabled'
      },
      scrollbar: {
        el: '.oa-product-image-slider__scrollbar',
        dragClass: 'oa-product-image-slider__scrollbar-drag',
        draggable: false
      },
      slidesPerView: 1,
      spaceBetween: 10,
      roundLengths: true,
      mousewheel: { forceToAxis: true, releaseOnEdges: true, invert: true },
      // Matches the slider product-single.layout.js builds, so a switched colour behaves the same.
      lazyLoad: { enabled: true, loadPrevNext: true, loadAllEarly: true },
      thumbs: thumbs
    });
  }

  // Re-bind the click handlers product-single.layout.js sets up in setupFashionView.
  function reinitSizeHandlers() {
    var $input = jQuery('#variant-id');
    var $addToCartBtn = jQuery('.oa-product-addtocart-btn');
    var $sizeBtnsSelected = jQuery('.fashion-info-sticky ul.product-sizes li.oa-product-sizes--item-size');

    // Length/logo switch handler
    jQuery('.product-sizes .oa-product-sizes--item').not('#cts-modal .product-sizes .oa-product-sizes--item, #product-stl .product-sizes .oa-product-sizes--item').off('click').on('click', function() {
      var $selectedSize = jQuery('.oa-product-sizes--item-size.active').not('#cts-modal .oa-product-sizes--item-size.active, #product-stl .oa-product-sizes--item-size.active').attr('data-size-name');
      var $sizeSelection = jQuery(this).attr('data-selection-size');
      var $fashionVariant = jQuery('.oa-product-sizes--item-size-' + $sizeSelection + '[data-size-name="' + $selectedSize + '"]').not('#cts-modal .oa-product-sizes--item-size-' + $sizeSelection + '[data-size-name="' + $selectedSize + '"], #product-stl .oa-product-sizes--item-size-' + $sizeSelection + '[data-size-name="' + $selectedSize + '"]');
      var $wrapper = jQuery('.product-sizes-wrapper').not('#product-stl .product-sizes-wrapper');

      jQuery('.product-sizes .oa-product-sizes--item').not('#cts-modal .product-sizes .oa-product-sizes--item, #product-stl .product-sizes .oa-product-sizes--item').removeClass('active');
      $sizeBtnsSelected.removeClass('active');
      jQuery(this).addClass('active');
      jQuery('.oa-single-product_meta-container .product-length-wrapper').not('#cts-modal .product-length-wrapper, #product-stl .product-length-wrapper').removeClass('red');

      if (jQuery('.oa-single-product_meta-container .oa-product-sizes--item-size').hasClass('product-sizes-pdp')) {
        jQuery('.product-sizes-pdp').not('#cts-modal .product-sizes-pdp, #product-stl .product-sizes-pdp').addClass('product-sizes-hidden');
        jQuery('.product-sizes-pdp.oa-product-sizes--item-size-' + $sizeSelection).not('#cts-modal .product-sizes-pdp.oa-product-sizes--item-size-' + $sizeSelection, '#product-stl .product-sizes-pdp.oa-product-sizes--item-size-' + $sizeSelection).removeClass('product-sizes-hidden');
      } else {
        jQuery('.oa-single-product_meta-container .oa-product-sizes--item-size').not('#cts-modal .oa-product-sizes--item-size, #product-stl .oa-product-sizes--item-size').hide();
        jQuery('.oa-single-product_meta-container .oa-product-sizes--item-size-' + $sizeSelection).not('#cts-modal .oa-product-sizes--item-size-' + $sizeSelection, '#product-stl .oa-product-sizes--item-size-' + $sizeSelection).show();
      }

      jQuery('.oa-single-product_meta-container .product-sizes-title-size').not('#cts-modal .product-sizes-title-size, #product-stl .product-sizes-title-size').show();

      $wrapper.removeClass();
      $wrapper.addClass('product-sizes-wrapper product-sizes').addClass(jQuery('.oa-product-logos--item.active').attr('data-selection-logo')).addClass(jQuery('.oa-product-sizes--item.active').attr('data-selection-size'));

      if ($selectedSize !== undefined && !$fashionVariant.hasClass('disabled')) {
        $fashionVariant.addClass('active');
        if (jQuery('.oa-product-sizes--item').not('#cts-modal .oa-product-sizes--item, #product-stl .oa-product-sizes--item').hasClass('active') && jQuery('.oa-product-sizes--item-size').not('#cts-modal .oa-product-sizes--item-size, #product-stl .oa-product-sizes--item-size').hasClass('active')) {
          $addToCartBtn.attr('disabled', false);
          jQuery('.oa-single-product_meta-container #oa-fashion .fashion-info-flex #in-den-warenkorb').not('#oa-fashion #cts-modal .fashion-info-flex #in-den-warenkorb, #oa-fashion #product-stl .fashion-info-flex #in-den-warenkorb').removeClass('select');
          jQuery('.oa-single-product_meta-container .product-sizes').not('#cts-modal .product-sizes, #product-stl .product-sizes').removeClass('red');
          jQuery('.oa-single-product_meta-container .choose-size-alert').not('#cts-modal .choose-size-alert, #product-stl .choose-size-alert').hide(100);
          $input.val(jQuery('.oa-product-sizes--item-size.active').not('#cts-modal .oa-product-sizes--item-size.active, #product-stl .oa-product-sizes--item-size.active').attr('data-size'));
        }
      } else {
        if (jQuery('.oa-single-product_meta-container .oa-product-sizes--item-size.active').not('#cts-modal .oa-product-sizes--item-size.active, #product-stl .oa-product-sizes--item-size.active').length === 0) {
          $input.val('');
        }
      }
      if (jQuery('.oa-single-product_meta-container .oa-product-sizes--item-size.active').not('#cts-modal .oa-product-sizes--item-size.active, #product-stl .oa-product-sizes--item-size.active').length === 0) {
        $input.val('');
      }
    });

    // Logo switch handler
    jQuery('.product-logos .oa-product-logos--item').off('click').on('click', function() {
      var $selectedLogo = jQuery('.oa-product-sizes--item-size.active').attr('data-size-name');
      var $sizeSelection = jQuery(this).attr('data-selection-size');
      var $logoSelection = jQuery(this).attr('data-selection-logo');
      var $fashionVariant = jQuery('.oa-product-sizes--item-size-' + $sizeSelection + '[data-size-name="' + $selectedLogo + '"]');
      var $wrapper = jQuery('.product-sizes-wrapper');

      jQuery('.product-logos .oa-product-logos--item').removeClass('active');
      $sizeBtnsSelected.removeClass('active');
      jQuery(this).addClass('active');
      jQuery('.product-length-wrapper').not('#product-stl .product-length-wrapper').removeClass('red');
      jQuery('.oa-product-sizes--item-size').hide();
      jQuery('.oa-product-sizes--item-size-' + $logoSelection).show();
      jQuery('.product-sizes-title-size').show();

      $wrapper.removeClass();
      $wrapper.addClass('product-sizes-wrapper product-sizes').addClass(jQuery('.oa-product-logos--item.active').attr('data-selection-logo')).addClass(jQuery('.oa-product-sizes--item.active').attr('data-selection-size'));

      if ($selectedLogo !== undefined && !$fashionVariant.hasClass('disabled')) {
        $fashionVariant.addClass('active');
        if (jQuery('.oa-product-logos--item').hasClass('active') && jQuery('.oa-product-sizes--item-size').hasClass('active')) {
          $addToCartBtn.attr('disabled', false);
          jQuery('#oa-fashion .fashion-info-flex #in-den-warenkorb').removeClass('select');
          jQuery('.product-logos').removeClass('red');
          jQuery('.choose-size-alert').hide(100);
        }
      } else {
        if (jQuery('.oa-product-sizes--item-size.active').length === 0) {
          $input.val('');
        }
      }
      if (jQuery('.oa-product-sizes--item-size.active').length === 0) {
        $input.val('');
      }
    });

    // Size button click handlers
    $sizeBtnsSelected.off('click').on('click', function() {
      var $this = jQuery(this);
      var variant_id = $this.data('size');

      if (!$this.hasClass('disabled')) {
        $sizeBtnsSelected.removeClass('active');
        $this.addClass('active');
        jQuery('.product-sizes-wrapper').not('#product-stl .product-sizes-wrapper').removeClass('red');
        if (jQuery('.oa-product-sizes--item-size').hasClass('active')) {
          jQuery('.choose-size-alert').hide(100);
        }
        if (variant_id) {
          $addToCartBtn.attr('disabled', false);
          $input.val(variant_id);
          jQuery('#oa-fashion .fashion-info-flex #in-den-warenkorb').removeClass('select');
          document.dispatchEvent(new CustomEvent('oa:variantchange', {
            detail: { sku: $this.data('sku'), id: $this.data('size'), name: $this.data('title'), sizeName: $this.data('size-name') }
          }));
        }
      }
    });

    // The add-to-cart gate is not re-bound here — reinitAtcHandlers owns it under
    // click.oaAtcGate. An unnamespaced copy used to live here too, and since neither .off()
    // removed the other both stayed attached and one click ran the gate twice.
  }

  function reselectSize(sizeName, lengthSelection, logoSelection) {
    // Length and logo are restored ahead of the size and independently of it: the fragment
    // carries the new colour's own default (length-detection picks the length with the most
    // in-stock sizes), which would otherwise move a shopper off the length they chose.
    if (lengthSelection) {
      var lengthBtn = document.querySelector('.fashion-info-sticky .oa-product-sizes--item[data-selection-size="' + lengthSelection + '"]:not(#cts-modal .oa-product-sizes--item)');
      if (lengthBtn) jQuery(lengthBtn).trigger('click');
    }

    if (logoSelection) {
      var logoBtn = document.querySelector('.product-logos .oa-product-logos--item[data-selection-logo="' + logoSelection + '"]');
      if (logoBtn) jQuery(logoBtn).trigger('click');
    }

    if (!sizeName) return;

    // The matching size button, if one is visible (not hidden by the length/logo filter) and
    // not disabled.
    var sizeButtons = document.querySelectorAll('.fashion-info-sticky .oa-product-sizes--item-size[data-size-name="' + sizeName + '"]:not(#cts-modal .oa-product-sizes--item-size)');
    for (var i = 0; i < sizeButtons.length; i++) {
      var btn = sizeButtons[i];
      if (!btn.classList.contains('product-sizes-hidden') && btn.offsetParent !== null && !btn.classList.contains('disabled')) {
        jQuery(btn).trigger('click');
        return;
      }
    }
  }

  function reinitAtcHandlers() {
    // Gate-keeper: block submit when no size is selected. Mirrors setupFashionView's own gate,
    // but scoped to the two blocks swapAtc owns — .oa-product-addtocart-btn also matches the
    // complete-the-look, shop-the-look and Top Picks buttons, which are Vue-rendered after
    // DOMContentLoaded and so escape the original pass. Document-wide, this one caught them and
    // preventDefault()ed their add-to-cart whenever #variant-id happened to be empty.
    jQuery(atcScope('.oa-product-addtocart-btn:not(.oa-product-addtocart-btn-cts)')).off('click.oaAtcGate').on('click.oaAtcGate', function(event) {
      if (jQuery('.oa-product-addtocart-btn').hasClass('early-access-cta')) return;
      var currentInput = jQuery('#variant-id');
      if (currentInput.val() === '') {
        event.preventDefault();
        if (jQuery('.oa-product-sizes--item').hasClass('active')) {
          jQuery('.product-sizes-wrapper').not('#product-stl .product-sizes-wrapper').addClass('red');
        } else if (jQuery('.oa-product-sizes--item-size').hasClass('active')) {
          jQuery('.product-length-wrapper').not('#product-stl .product-length-wrapper').addClass('red');
        } else {
          jQuery('.product-sizes').not('#product-stl .product-sizes').addClass('red');
        }
        var sizeAlert = jQuery('.choose-size-alert');
        sizeAlert.show(100);
        window.oaAnnounceEl && window.oaAnnounceEl(sizeAlert.first());
      }
    });

    // Mobile sticky ATC scroll-up. Mirrors setupFashionView's
    // `.is-sticky #in-den-warenkorb.select` handler, scoped for the same reason as the gate above.
    jQuery('#oa-switch-atc-sticky #in-den-warenkorb.select').off('click.oaAtcScroll').on('click.oaAtcScroll', function() {
      if (!jQuery(this).hasClass('select')) return;
      var fashionFlex = jQuery('.fashion-info-flex');
      if (!fashionFlex.length) return;
      jQuery([document.documentElement, document.body]).animate({
        scrollTop: fashionFlex.offset().top - 100
      }, 500);
    });
  }

  // What product-single.layout.js and size-chart.liquid bind per element at DOMContentLoaded, on
  // nodes the swaps replace. Re-declared rather than re-running the originals, which bind the
  // rest of the page too and would double up every handler that did survive.
  function rebindStaticHandlers() {
    if (!window.jQuery) return;

    // Description tabs. Mirrors setupTabs in product-single.layout.js.
    var desktopTabs = document.querySelectorAll('.desktop-tab');
    for (var t = 0; t < desktopTabs.length; t++) {
      bindTabHeadlines(desktopTabs[t]);
    }

    // Quantity stepper. Mirrors the `.oa-qty-input` block in product-single.layout.js. Namespaced so a switch that
    // somehow runs against a surviving stepper re-points rather than stacks.
    var $qtyGroup = jQuery('.oa-qty-input');
    var $qtyInput = $qtyGroup.find('.qty-input');
    var maxQty = 50;
    $qtyGroup.find('.btn-inc').off('click.oaQty').on('click.oaQty', function() {
      var val = parseInt($qtyInput.val(), 10) || 0;
      $qtyInput.val(Math.min(val + 1, maxQty));
    });
    $qtyGroup.find('.btn-dec').off('click.oaQty').on('click.oaQty', function() {
      var val = parseInt($qtyInput.val(), 10) || 0;
      if (val > 1) $qtyInput.val(val - 1);
    });
    $qtyInput.off('change.oaQty').on('change.oaQty', function() {
      if ($qtyInput.val() > maxQty) $qtyInput.val(maxQty);
    });

    // Desktop click-to-zoom. Mirrors the `.oa-picture__img` click listener in product-single.layout.js.
    var zoomables = document.querySelectorAll('.oa-fashion__product-image-container .oa-picture__img, .oa-fashion__product-image-container .oa-video-wrapper video');
    for (var z = 0; z < zoomables.length; z++) {
      zoomables[z].addEventListener('click', onZoomableClick, { passive: true });
    }
  }

  function onZoomableClick(event) {
    if (window.oaProductZoom) window.oaProductZoom.show(event.target, event);
  }

  function bindTabHeadlines(desktopTab) {
    var headlines = desktopTab.querySelectorAll('.desktop-tab-headline[data-tab]');
    for (var i = 0; i < headlines.length; i++) {
      bindTabHeadline(desktopTab, headlines[i]);
    }
  }

  function bindTabHeadline(desktopTab, headline) {
    // swapTabs is a no-op when the fragment carries no .desktop-tab, and a second closure on a
    // surviving headline would slideToggle twice per click — open, then immediately close. The
    // bindings above are idempotent by construction; this one is not.
    if (headline.oaTabBound) return;
    headline.oaTabBound = true;

    headline.addEventListener('click', function(event) {
      var tabId = headline.getAttribute('data-tab');
      // Usually the next sibling; fall back to a lookup rather than dereferencing it unguarded.
      var next = headline.nextElementSibling;
      var tabContent = next && next.classList.contains('desktop-tab-content') && next.getAttribute('data-tab-content') === tabId
        ? next
        : desktopTab.querySelector('.desktop-tab-content[data-tab-content="' + tabId + '"]');
      if (!tabContent) return;
      event.preventDefault();
      jQuery(tabContent).slideToggle(225);
      headline.classList.toggle('active');
    });
  }

  // Re-arm the sold-out sizes that open the back-in-stock sidebar: setupNotifyMe is only included
  // when the LANDING colour had a sold-out variant, and binds onto the <li>s a switch replaces.
  // The sidebar holds the landing colour's variants as a prop, so the new ones go through the
  // plpVariants / isPlpContext path — the one path that ignores it — before `show`.
  function reinitNotifyHandlers(product) {
    if (!window.jQuery || !window.sidebarBackInStock) return;

    var unavailable = (product.variants || []).filter(function(v) { return !v.available; });
    if (!unavailable.length) return;

    var $tiles = jQuery('.fashion-info-sticky .oa-product-sizes--item-size.disabled')
      .not('#cts-modal .oa-product-sizes--item-size, #product-stl .oa-product-sizes--item-size');

    // The badge is server-rendered on first paint; tiles rebuilt by updateSizes carry none.
    $tiles.each(function() {
      if (!this.querySelector('.notifyMe')) jQuery(this).prepend('<span class="notifyMe"></span>');
    });

    $tiles.off('click.oaSwitchNotify').on('click.oaSwitchNotify', function() {
      openNotifySidebar(unavailable, jQuery(this).data('size'), product.id);
    });
  }

  // Hand the sidebar the colour actually on screen. Also used by the delegated .btn-soldout
  // handler in init(), the only entry point left for a colour sold out in every size —
  // product-fashion-size-input renders the picker under {% if product.available %}, so there are
  // no tiles to bind.
  function openNotifySidebar(unavailableVariants, variantId, productId) {
    var sidebar = window.sidebarBackInStock;
    if (!sidebar || !sidebar._data) return;
    if (unavailableVariants && unavailableVariants.length) {
      sidebar._data.plpVariants = unavailableVariants;
      sidebar._data.isPlpContext = true;
    }
    if (productId) sidebar._data.contextProductId = productId;
    if (variantId) sidebar._data.formData.variant = variantId;
    sidebar._data.hideForm = false;
    sidebar._data.show = true;
  }

  // The wishlist app claims .btn--addtowishlist-desktop and never re-reads its data-* attributes,
  // so the node is replaced rather than rewritten — it does mount nodes that appear after boot.
  // Mobile needs nothing: its mount point is inside #oa-switch-mobile-images and goes with it.
  function swapWishlist(doc) {
    var src = doc.querySelector('#oa-switch-wishlist-desktop .btn--addtowishlist-desktop');
    var live = document.querySelector('.btn--addtowishlist-desktop');
    if (!src || !live || !live.parentNode) return;
    live.parentNode.replaceChild(document.importNode(src, true), live);
  }

  function updateTracking(product) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'virtualPageview',
        page: '/products/' + product.handle,
        productId: product.id,
        productTitle: product.title
      });
    }

    // Answered against the .nosto_product tagging swapNostoTagging has already replaced; without
    // that this returned the landing colour's recommendations every time. The answer arrives
    // through global.js's postrender listener, which refreshes "Complete the look".
    nostojs(function(api) { api.loadRecommendations(); });

    // Yotpo points are updateYotpoPoints's job. Rewriting its data-yotpo-* attributes does
    // nothing — the widget reads them at render and never looks again (its config sets neither
    // should-lazy-load nor should-watch-mutations).
  }

  // --- Yotpo points figure ---
  //
  // The logged-in reward-points module is Yotpo's, so only its digits are rewritten: re-rendering
  // it from the fragment draws Yotpo's empty state at 0px and the module vanishes. The rate is
  // points-per-cent measured from first paint, not assumed 1:1, so a VIP multiplier survives; if
  // it cannot be established the number is left alone.
  var yotpoPointsRate = null;
  // The colour the page was served with: the rendered figure belongs to it, so the rate is
  // measured against this and not currentHandle, which has moved on by the second switch.
  var pointsBaselineHandle = null;

  function yotpoPointsTextNode() {
    var el = document.querySelector('.yotpo-product-points-container [data-yotpo-instance-id]');
    if (!el) return null;
    // Walk to the first text node with a digit rather than depend on Yotpo's class or element
    // shape. <style>/<script> are skipped — the widget injects its own styling, and an unfiltered
    // walk would return a CSS length.
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: function(node) {
        var tag = node.parentNode && node.parentNode.nodeName;
        if (tag === 'STYLE' || tag === 'SCRIPT') return NodeFilter.FILTER_REJECT;
        return /\d/.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    }, false);
    return walker.nextNode();
  }

  // Digits plus any grouping separator, and no trailing whitespace: the string is "5000 points",
  // so eating the space would splice the replacement onto the word.
  var POINTS_PATTERN = /\d[\d.,]*/;

  function parsePoints(text) {
    var match = text.match(POINTS_PATTERN);
    if (!match) return null;
    var digits = match[0].replace(/\D/g, '');
    return digits ? parseInt(digits, 10) : null;
  }

  // Re-apply whatever grouping Yotpo used, so a switch cannot change 5,000 into 5000.
  function formatPointsLike(sample, value) {
    var separator = sample.indexOf(',') !== -1 ? ',' : (sample.indexOf('.') !== -1 ? '.' : '');
    var text = String(value);
    return separator ? text.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : text;
  }

  // True while the rate is still unknown and there is a module to measure — the one case where
  // switchColor needs the landing colour's price as well as the new one.
  function yotpoBaselineNeeded() {
    return yotpoPointsRate === null && !!pointsBaselineHandle && !!yotpoPointsTextNode();
  }

  function updateYotpoPoints(product, baselineProduct) {
    var node = yotpoPointsTextNode();
    if (!node) return;

    if (yotpoPointsRate === null) {
      // Baseline fetched through OAProductsApi like the new price, so both are the same units.
      if (!baselineProduct || !baselineProduct.price) return;
      var shown = parsePoints(node.nodeValue);
      if (!shown) return;
      var rate = shown / baselineProduct.price;
      if (!isFinite(rate) || rate <= 0) return;
      yotpoPointsRate = rate;
    }

    if (!product.price) return;
    var sample = (node.nodeValue.match(POINTS_PATTERN) || [''])[0];
    node.nodeValue = node.nodeValue.replace(
      POINTS_PATTERN,
      formatPointsLike(sample, Math.round(product.price * yotpoPointsRate))
    );
  }

  // .d-none is display:none !important (oner_m.scss.liquid:10862).
  function toggleAll(selector, hidden) {
    var els = document.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) els[i].classList.toggle('d-none', hidden);
  }

  // The launch tags and the delayed-launch tags (theme.liquid) both suppress the buy controls.
  function buyControlHideTags() {
    return (window.oaComingSoonHideTags || []).concat(window.oaDelayedHideTags || []);
  }

  function hidesBuyControls(tags) {
    return buyControlHideTags().some(function (t) { return hasTag(tags, t); });
  }

  // Delayed colours launch after countdown_to, so they never show the countdown.
  function isDelayed(tags) {
    return (window.oaDelayedHideTags || []).some(function (t) { return hasTag(tags, t); });
  }

  function updateEarlyAccessState(product) {
    if (!buyControlHideTags().length) return;

    var isComingSoon = hidesBuyControls(product.tags);

    // The server-rendered <style id="oa-coming-soon-styles">, which hides the add-to-cart block
    // and the stock line for coming-soon products. The size picker is not rendered for them at all.
    var styleEl = document.getElementById('oa-coming-soon-styles');
    if (styleEl && styleEl.sheet) {
      styleEl.sheet.disabled = !isComingSoon;
    }

    // The d-none toggles are the real mechanism, not a supplement to it: product.fashion.liquid
    // only emits that stylesheet when the colour the shopper LANDED on is coming-soon, so on the
    // way in from a live colour there is nothing to disable and every rule has to be reproduced
    // here — the stock line included, or "In Stock" sits on a colour that is not yet purchasable.
    toggleAll('.product-add-to-cart', isComingSoon);
    toggleAll('.oa-single-product .product-stock .product-instock', isComingSoon);
    toggleAll('.oa-coming-soon-notify', !isComingSoon);

    var countdown = document.getElementById('oa-product-sale-countdown');
    if (countdown) {
      countdown.style.display = isComingSoon && !isDelayed(product.tags) ? '' : 'none';
    }
  }

  // --- Loading State ---

  function showLoading() {
    var el = document.getElementById('oa-fashion');
    if (el) el.classList.add('oa-color-switching');
  }

  function hideLoading() {
    var el = document.getElementById('oa-fashion');
    if (el) el.classList.remove('oa-color-switching');
  }

  // --- Main API ---

  function switchColor(handle, opts) {
    opts = opts || {};
    if (!handle) return;
    // Queue rather than drop a click whose navigation onSwatchClick has already suppressed; opts
    // rides along so a queued popstate is not replayed as a fresh history entry. Checked before
    // the same-colour return below: currentHandle is still the colour on screen while a switch is
    // in flight, so a Forward onto it is a real navigation, not a no-op.
    if (switching) {
      queuedHandle = handle;
      queuedOpts = opts;
      // The browser has already moved the address bar, so the in-flight switch gives up its
      // history entry — pushing on top of it stranded the URL, canonical link and title on a
      // colour the DOM no longer showed, and a reload then produced a different page.
      if (opts.pushState === false) suppressPush = true;
      return;
    }
    if (handle === currentHandle) return;
    // A failed asset load would otherwise throw synchronously here, after the click was already
    // suppressed — a dead swatch rather than a fallback.
    if (!window.OAProductsApi) { window.location.href = '/products/' + handle; return; }

    switching = true;
    suppressPush = false;
    showLoading();

    var productFetch, htmlFetch, baselineFetch;
    // This all runs synchronously, and a throw here left `switching` stuck true with no .then()
    // to clear it — every later click was then queued and never applied, leaving the colour rail
    // dead for the session with not even a full page load as a fallback.
    try {
      productFetch = window.OAProductsApi.fetchByHandles([handle]);
      htmlFetch = fetchSwitchHtml(handle);
      // The landing colour's price, needed once for Yotpo's points-per-cent rate. Runs alongside
      // the other two so it costs no wall-clock, and a failure only leaves the points figure as
      // Yotpo rendered it.
      baselineFetch = yotpoBaselineNeeded()
        ? window.OAProductsApi.fetchByHandles([pointsBaselineHandle])
            .then(function(r) { return r && r[0]; })
            .catch(function() { return null; })
        : Promise.resolve(null);
    } catch (err) {
      console.error('Color switch error:', err);
      switching = false;
      hideLoading();
      window.location.href = '/products/' + handle;
      return;
    }

    Promise.all([productFetch, htmlFetch, baselineFetch]).then(function(results) {
      var product = results[0] && results[0][0];
      var html = results[1];
      var baselineProduct = results[2];

      if (!product || !html) {
        window.location.href = '/products/' + handle;
        return;
      }

      // A coming-soon colour is served with its size picker and add-to-cart suppressed, and what
      // stands in for them — #oa-product-sale-countdown — is painted outside every swap region.
      // With none on the page (the landing colour was not part of the countdown campaign) the
      // switch would leave nothing to buy and nothing to wait for, so let the server render it.
      // Delayed colours take no countdown, so they swap without one.
      if (hidesBuyControls(product.tags) && !isDelayed(product.tags) &&
          !document.getElementById('oa-product-sale-countdown')) {
        window.location.href = '/products/' + handle;
        return;
      }

      var doc = new DOMParser().parseFromString(html, 'text/html');

      // Capture the current selection before the picker is replaced, so it can be restored.
      var previousSizeName = null;
      var previousLength = null;
      var previousLogo = null;
      var activeSize = document.querySelector('.fashion-info-sticky .oa-product-sizes--item-size.active:not(#cts-modal .oa-product-sizes--item-size)');
      if (activeSize) previousSizeName = activeSize.getAttribute('data-size-name');
      var activeLength = document.querySelector('.fashion-info-sticky .oa-product-sizes--item.active:not(#cts-modal .oa-product-sizes--item)');
      if (activeLength) previousLength = activeLength.getAttribute('data-selection-size');
      var activeLogo = document.querySelector('.product-logos .oa-product-logos--item.active');
      if (activeLogo) previousLogo = activeLogo.getAttribute('data-selection-logo');

      // All DOM replacement happens here, before anything binds below: a handler bound to a node
      // a later swap replaces would silently operate on a detached element.
      swapColorName(doc, handle);
      swapTop(doc);
      swapBreadcrumb(doc);
      swapDesktopImages(doc);
      swapZoomGallery(doc);
      swapMobileImages(doc);
      swapSizes(doc);
      swapStock(doc);
      swapPrice(doc);
      swapPoints(doc);
      swapAtc(doc);
      swapWishlist(doc);
      swapTabs(doc);
      swapNostoTagging(doc);
      swapHeadMeta(doc);

      // Re-bind interactive behaviour on the freshly swapped nodes, then apply the updates that
      // are attribute-level rather than markup.
      reinitSizeHandlers();
      reinitAtcHandlers();
      rebindStaticHandlers();
      reselectSize(previousSizeName, previousLength, previousLogo);
      reinitNotifyHandlers(product);
      swapFitFinder(doc);
      updateEarlyAccessState(product);
      updateFormProductId(product);
      updateActiveColorSwatch(handle);
      updateUrl(handle, suppressPush ? false : opts.pushState, doc);
      updateTracking(product);
      updateYotpoPoints(product, baselineProduct);

      currentHandle = handle;
      currentProduct = product;

      document.dispatchEvent(new CustomEvent('oa:colorswitch', { detail: { product: product, handle: handle } }));

    }).catch(function(err) {
      console.error('Color switch error:', err);
      window.location.href = '/products/' + handle;
    }).then(function() {
      switching = false;
      hideLoading();

      // Only the most recent queued colour is applied — the ones the shopper clicked past aren't
      // worth a round trip.
      if (queuedHandle && queuedHandle !== currentHandle) {
        var next = queuedHandle;
        var nextOpts = queuedOpts;
        queuedHandle = null;
        queuedOpts = null;
        switchColor(next, nextOpts);
      } else {
        queuedHandle = null;
        queuedOpts = null;
      }
    });
  }

  // Warm both caches so a later switchColor resolves from memory. Skips the current colour and
  // de-dupes through them, so calling it repeatedly on hover is cheap. Takes a handle or an array.
  function preload(handles) {
    if (!handles) return;
    if (typeof handles === 'string') handles = [handles];
    var list = handles.filter(function(h) { return h && h !== currentHandle; });
    if (!list.length) return;
    if (window.OAProductsApi) window.OAProductsApi.fetchByHandles(list);
    list.forEach(function(h) { fetchSwitchHtml(h); });
  }

  // Resolve the product handle for a node inside a colour swatch; the anchor's href is the
  // fallback. Returns null for boxes that aren't swatches — the "+N Colors" toggle, which keeps
  // its own inline handler.
  function swatchHandle(node) {
    if (!node || !node.closest) return null;
    var box = node.closest('.sidebar--color-item-box');
    if (!box) return null;

    var bg = box.querySelector('.sidebar--color-item-bg[data-handle]');
    var raw = bg && bg.getAttribute('data-handle');
    if (!raw) {
      var link = box.querySelector('a[href*="/products/"]');
      raw = link && link.getAttribute('href');
    }
    if (!raw) return null;

    return normalizeHandle(raw) || null;
  }

  // Warm on first intent rather than fetching the whole colour family on load, so shoppers who
  // never switch pay nothing. Delegated on document to cover swatches rendered after load (the
  // async Algolia render, the "+N Colors" reveal); lastPrefetch skips re-walking the DOM while
  // the pointer sits on one swatch. pointerover covers mouse/pen, focusin keyboard, touchstart
  // gives touch a head start just before the tap.
  function onSwatchIntent(e) {
    var t = e.target;
    if (!t || !t.closest || !t.closest(SWATCH_CONTAINERS)) return;
    var handle = swatchHandle(t);
    if (!handle || handle === currentHandle || handle === lastPrefetch) return;
    lastPrefetch = handle;
    preload(handle);
  }

  // Delegated: the rails are rebuilt client-side, so there is no moment at which every swatch
  // exists to bind. Modifier and middle clicks fall through to the href, keeping "open in new
  // tab". Capture phase because a third-party tag navigates by assigning location on bubble,
  // which preventDefault cannot stop — the early returns run before propagation is stopped.
  function onSwatchClick(event) {
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button !== 0) return;
    var t = event.target;
    if (!t || !t.closest || !t.closest(SWATCH_CONTAINERS)) return;
    var handle = swatchHandle(t);
    if (!handle) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    switchColor(handle);
  }

  function init() {
    // Only the fashion PDP paints the #oa-switch-* regions. product.liquid renders the same
    // colour rail (and so loads this file), but no regions: every swap would no-op and leave the
    // old picker's variant ids behind, so let the swatch links navigate instead.
    if (!document.getElementById('oa-switch-sizes')) return;

    currentHandle = getHandleFromUrl();
    // Pinned to the served colour and never moved: the figure Yotpo rendered belongs to it.
    pointsBaselineHandle = currentHandle;
    if (currentHandle) {
      history.replaceState({ handle: currentHandle }, '', window.location.href);
    }

    window.addEventListener('popstate', function(e) {
      if (e.state && e.state.handle) {
        switchColor(e.state.handle, { pushState: false });
      } else {
        var h = getHandleFromUrl();
        if (h) switchColor(h, { pushState: false });
      }
    });

    // Intent-based prefetch (see onSwatchIntent).
    ['pointerover', 'focusin', 'touchstart'].forEach(function(ev) {
      document.addEventListener(ev, onSwatchIntent, { passive: true });
    });

    document.addEventListener('click', onSwatchClick, true);

    // Sold-out buttons, which may be injected later. currentProduct is null until the first
    // switch, where the sidebar's mounted-with variants are already the right ones — so null is
    // passed deliberately.
    document.addEventListener('click', function(e) {
      var btn = e.target.closest && e.target.closest('.btn-soldout');
      if (!btn) return;
      if (!window.sidebarBackInStock) return;
      e.preventDefault();
      var unavailable = currentProduct
        ? (currentProduct.variants || []).filter(function(v) { return !v.available; })
        : null;
      openNotifySidebar(unavailable, null, currentProduct && currentProduct.id);
    });
  }

  // Loading state.
  var style = document.createElement('style');
  style.textContent = '#oa-fashion.oa-color-switching .oa-fashion__product-image-container,' +
    '#oa-fashion.oa-color-switching .oa-product-image-slider,' +
    '#oa-fashion.oa-color-switching .product-top-wrapper,' +
    '#oa-fashion.oa-color-switching .oa-product_price-section,' +
    '#oa-fashion.oa-color-switching .product-sizes-container,' +
    '#oa-fashion.oa-color-switching .product-add-to-cart {' +
    'opacity:0.5;transition:opacity 0.15s;pointer-events:none;}';
  document.head.appendChild(style);

  window.oaPdpColorSwitch = {
    init: init,
    switchColor: switchColor,
    preload: preload
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
